import type { FormSubmitPayload, FormSubmitValues, FormWidgetConfig } from './form-widget.types';
import { isFieldVisible } from './form-visibility';
import {
  getIdentificationType,
  normalizeIdentificationCountry,
  normalizeIdentificationNumber,
} from './identification';

export function createDefaultValues(
  config: FormWidgetConfig,
  prefillValues?: FormSubmitValues,
): FormSubmitValues {
  const values: FormSubmitValues = {};

  config.fields.forEach((field) => {
    if (field.type === 'checkbox') {
      values[field.name] = Boolean(field.defaultValue);
      return;
    }

    values[field.name] = typeof field.defaultValue === 'string' ? field.defaultValue : '';
  });

  if (config.privacy.enabled) {
    values.privacyAccepted = false;
  }

  if (prefillValues) {
    config.fields.forEach((field) => {
      const value = prefillValues[field.name];
      if (value === undefined) return;

      // El país llega crudo desde el legacy ("Otro", "Chile"): si no se
      // interpreta acá, el select recibe un valor que no es ninguna de sus
      // opciones y la persona queda con un documento que nunca calza.
      if (field.name === 'identificationCountry') {
        const country = normalizeIdentificationCountry(value);
        if (country !== null) values[field.name] = country;
        return;
      }

      values[field.name] = value;
    });
  }

  return values;
}

export function createSubmitPayload(
  config: FormWidgetConfig,
  values: FormSubmitValues,
  options: {
    source?: string;
    trackingId?: string;
    pageUrl?: string;
    accountFieldNames?: string[];
    widgetFieldNames?: string[];
    extraFields?: FormSubmitValues;
    recaptchaToken?: string;
  } = {},
): FormSubmitPayload {
  const source = options.source ?? config.context?.source;
  const fields = config.fields.reduce<FormSubmitValues>((acc, field) => {
    if (!isFieldVisible(field, values)) return acc;

    const value = values[field.name];
    if (value === '' || value === undefined) return acc;

    acc[field.name] = value;
    return acc;
  }, {});

  if (config.privacy.enabled && typeof values.privacyAccepted === 'boolean') {
    fields.privacyAccepted = values.privacyAccepted;
  }

  if (
    typeof fields.identificationCountry === 'string' &&
    typeof fields.identificationNumber === 'string'
  ) {
    const identificationNumberFromAccount = options.accountFieldNames?.includes('identificationNumber');

    fields.identificationType = getIdentificationType(
      fields.identificationCountry,
      fields.identificationNumber,
    );
    if (!identificationNumberFromAccount) {
      fields.identificationNumber = normalizeIdentificationNumber(
        fields.identificationCountry,
        fields.identificationNumber,
      );
    }
  }

  if (options.extraFields) {
    Object.entries(options.extraFields).forEach(([key, value]) => {
      if (value === '' || value === undefined) return;
      if (fields[key] === undefined) fields[key] = value;
    });
  }

  const pickFields = (fieldNames?: string[]) => {
    if (!fieldNames?.length) return undefined;

    const picked = fieldNames.reduce<FormSubmitValues>((acc, name) => {
      const value = fields[name];
      if (value === '' || value === undefined) return acc;
      acc[name] = value;
      return acc;
    }, {});

    return Object.keys(picked).length > 0 ? picked : undefined;
  };

  const accountFields = pickFields(options.accountFieldNames);
  const widgetFields = pickFields(options.widgetFieldNames);

  return {
    platformCountry: config.country,
    variant: config.variant,
    ...(source ? { source } : {}),
    ...(options.trackingId ? { trackingId: options.trackingId } : {}),
    ...(options.pageUrl ? { pageUrl: options.pageUrl } : {}),
    fields,
    ...(accountFields ? { accountFields } : {}),
    ...(widgetFields ? { widgetFields } : {}),
    ...(options.recaptchaToken ? { recaptchaToken: options.recaptchaToken } : {}),
    submittedAt: new Date().toISOString(),
  };
}
